import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
import { roleSchema } from './role.js';
/**
 * `User` — une seule table pour toutes les populations (M6) ; colonnes et contraintes dans
 * `docs/diagrams/mcd-ghp.mermaid`. `lastName`/`firstName` nullables : un compte d'équipe est
 * partagé, sans titulaire nommé.
 *
 * ⚠️ `hash` ne traverse **jamais** la frontière réseau — passer par `publicUserSchema` ci-dessous,
 * jamais par ce schéma entier.
 */
export const userSchema = z.object({
    id: uuidV7Schema,
    role: roleSchema,
    login: z.string().min(1),
    hash: z.string().min(1),
    active: z.boolean(),
    lastName: z.string().nullable(),
    firstName: z.string().nullable(),
    /**
     * L'adresse où part le lien « Mot de passe oublié », **et rien d'autre** — facultative, et
     * jamais sur un compte d'équipe, partagé, que le bureau réinitialise (M6, D3).
     */
    email: z.email().nullable(),
});
/**
 * Posé une fois ici : un `omit` réécrit par chaque appelant s'oublie, et expose des hachages.
 * Sans `email` non plus : un gestionnaire lit les membres des équipes, il n'a pas à lire leurs
 * adresses — seul l'admin les voit, par `userAccountSchema`.
 */
export const publicUserSchema = userSchema.omit({ hash: true, email: true });
/**
 * Ce que l'admin lit d'un compte dans « Utilisateurs & rôles » : la ligne publique, l'adresse, et
 * la demande « Mot de passe oublié » qui attend qu'il renvoie l'accès (#627).
 */
export const userAccountSchema = userSchema
    .omit({ hash: true })
    .extend({ passwordResetRequestedAt: z.iso.datetime().nullable() });
/**
 * Ce que rendent la création d'un compte et la réinitialisation d'un mot de passe : la ligne
 * publique, **et le mot de passe en clair**.
 *
 * ⚠️ **C'est la seule occasion de le lire.** Seul son hachage est stocké : personne ne le
 * retrouve ensuite, pas même un admin — qui réinitialise s'il l'a perdu. À l'admin de le
 * transmettre hors bande, et à ce qui le traverse de ne pas le journaliser.
 */
export const issuedCredentialsSchema = z.object({
    user: publicUserSchema,
    password: z.string().min(1),
});
/**
 * Ce que l'admin fournit pour créer un compte (M6). **Ni `login` ni mot de passe** : le premier
 * est dérivé, le second tiré au hasard côté serveur. L'union discriminée porte l'invariant que
 * `userSchema` ne peut pas dire — un compte d'équipe se construit sur des initiales, pas un nom.
 *
 * ⚠️ **`strictObject`, pas `object`** : le `strip` par défaut retirerait un `password` de trop
 * en silence, et l'appelant croirait l'avoir imposé.
 */
export const createUserSchema = z.discriminatedUnion('role', [
    z.strictObject({
        role: z.literal('team'),
        initials: z.tuple([z.string().min(1), z.string().min(1)]),
    }),
    z.strictObject({
        // Dérivé de `roleSchema`, pas réénuméré : un 6ᵉ rôle ajouté à `ROLES` entre ici de
        // lui-même, au lieu d'être refusé par une liste que personne n'a pensé à rallonger.
        role: roleSchema.exclude(['team']),
        lastName: z.string().min(1),
        firstName: z.string().min(1),
        email: z.email().optional(),
    }),
]);
/**
 * Ce qu'un admin peut **corriger** sur un compte existant (M6). Ce que ce schéma ne porte pas
 * est aussi décidé que ce qu'il porte :
 *
 * - **`login`** — « généré une fois, puis stocké, jamais recalculé » (M6). Corriger
 *   l'orthographe d'un nom ne doit pas changer l'identifiant de connexion de quelqu'un.
 * - **`active`** — la fermeture d'un compte coupe les sessions et défend le dernier admin
 *   actif ; entrée ici, elle deviendrait un champ parmi d'autres, basculé par un `PATCH` de
 *   formulaire qui renvoie tout son état.
 * - **le mot de passe** — il se tire, il ne se fournit pas, et pour la même raison que sur
 *   `createUserSchema` : `strictObject` refuse bruyamment celui qui le tenterait.
 * - **`role: 'team'`** — un compte d'équipe n'a ni titulaire ni login nominatif. Faire passer
 *   un rôle vers `team`, ou l'en sortir, demanderait un autre `login` : c'est un autre compte.
 *
 * ⚠️ **Au moins un champ.** Un `PATCH` vide n'est pas une saisie douteuse à enregistrer (M21)
 * mais un appel qui ne demande rien : l'accepter masquerait le bug de l'appelant, et Drizzle
 * refuse de toute façon un `set({})`.
 */
export const updateUserSchema = z
    .strictObject({
    // Dérivé de `roleSchema`, comme sur `createUserSchema` : un 6ᵉ rôle entre de lui-même.
    role: roleSchema.exclude(['team']).optional(),
    lastName: z.string().min(1).optional(),
    firstName: z.string().min(1).optional(),
    // `null` efface : l'adresse est facultative, la retirer est une correction comme une autre.
    email: z.email().nullable().optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, { error: 'aucun champ à modifier' });
//# sourceMappingURL=user.js.map